import {
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Injectable,
  inject,
  signal,
} from '@angular/core';
import {
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { API_V1_URL } from '../../../core/config/api.config';
import {
  ArtikelkatalogEintrag,
  Wareneingang,
  WareneingangDetail,
} from '../models/wareneingang.model';
import { WareneingangPosition } from '../models/wareneingang-position.model';
import { WareneingangStatus } from '../models/wareneingang-status.enum';
import { ArtikelEinheitDto, normalizeArtikelEinheiten } from '../../../shared/models/artikel-einheit.model';

interface BackendWareneingangDto {
  uid: string;
  filialNummer: number;
  filialName: string | null;
  wareneingangTag: string;
  beschreibung: string;
  lieferscheinnummer: string | null;
  bearbeitungsZeitBis: string;
  restzeit: string;
  anzahlPositionen: number;
  anzahlOffenePositionen: number;
  fortschrittProzent: number;

  status:
  | 'Offen'
  | 'InBearbeitung'
  | 'Abgeschlossen';

  faelligkeit:
  | 'Ueberfaellig'
  | 'HeuteFaellig'
  | 'Zukuenftig'
  | 'Keine';
}

interface BackendWareneingangDetailDto {
  uid: string;
  filialNummer: number;
  filialName: string | null;
  wareneingangTag: string;
  beschreibung: string;
  lieferscheinnummer: string | null;
  bearbeitungsZeitBis: string;
  restzeit: string;
  abgeschlossen: boolean;

  status:
  | 'Offen'
  | 'InBearbeitung'
  | 'Abgeschlossen';

  anzahlPositionen: number;
  liefermengeGesamt: number;
  istMengeGesamt: number;
  anzahlKorrekturen: number;

  positionen:
  BackendWareneingangPositionDto[];
}

interface BackendWareneingangPositionDto {
  artikelnummer: number;
  artikelName: string | null;
  warengruppeId: number | null;
  warengruppeName: string | null;
  bestellteMenge: number;
  lieferMenge: number;
  istMenge: number;
  offeneMenge: number;
  abweichung: number;
  verkaufspreis: number | null;
  lieferEinheitName: string | null;
  sortierschluessel: string | null;
}

interface BackendVerfuegbarerArtikelDto {
  einheiten?: readonly ArtikelEinheitDto[];
  artikelnummer: number;
  artikelName: string | null;
  warengruppeId: number | null;
  lieferEinheitName: string | null;
}

export interface WareneingangLockSession {
  lockId: string;
  expiresAt: string;
}

interface LockConflictResponse {
  blockedKeys?: {
    lockedByName: string;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class WareneingaengeService {
  private readonly http =
    inject(HttpClient);

  private readonly apiBaseUrl =
    API_V1_URL;

  private readonly activeLocks =
    new Map<
      string,
      WareneingangLockSession
    >();

  private readonly anzahlOffenState =
    signal<
      number |
      undefined
    >(undefined);

  readonly anzahlOffen =
    this.anzahlOffenState
      .asReadonly();

  getWareneingaenge(): Observable<
    Wareneingang[]
  > {
    return this.http
      .get<
        BackendWareneingangDto[]
      >(
        `${this.apiBaseUrl}/wareneingaenge/all`
      )
      .pipe(
        map((items) =>
          items.map(
            (item) =>
              this.mapWareneingang(
                item
              )
          )
        )
      );
  }

  getAnzahlOffen(): Observable<number> {
    return this.http
      .get<number>(
        `${this.apiBaseUrl}/wareneingaenge/anzahl-offen`
      )
      .pipe(
        tap((anzahl) => {
          this.anzahlOffenState.set(
            anzahl
          );
        })
      );
  }

  getWareneingangById(
    id: string
  ): Observable<
    WareneingangDetail |
    null
  > {
    return this.http
      .get<
        BackendWareneingangDetailDto
      >(
        `${this.apiBaseUrl}/wareneingaenge/${id}`
      )
      .pipe(
        map((item) =>
          this.mapWareneingangDetail(
            item
          )
        )
      );
  }

  acquireLock(
    id: string,
    _userName: string
  ): Observable<{
    success: boolean;
    detail:
    WareneingangDetail |
    null;
    lockedBy?:
    string |
    null;
  }> {
    const key =
      `wareneingang:${id}`;

    return this.http
      .post<
        WareneingangLockSession
      >(
        `${this.apiBaseUrl}/locks/acquire`,

        {
          keys: [key],
        }
      )
      .pipe(
        tap((response) => {
          this.activeLocks.set(
            key,
            response
          );
        }),

        switchMap(() =>
          this.getWareneingangById(
            id
          )
        ),

        map((detail) => {
          if (!detail) {
            return {
              success:
                false,

              detail:
                null,

              lockedBy:
                null,
            };
          }

          return {
            success:
              true,

            detail: {
              ...detail,

              istGesperrt:
                true,

              gesperrtVon:
                _userName,
            },

            lockedBy:
              null,
          };
        }),

        catchError(
          (
            error:
              HttpErrorResponse
          ) => {
            const conflict =
              error.error as
              LockConflictResponse |
              null;

            return of({
              success:
                false,

              detail:
                null,

              lockedBy:
                error.status ===
                  423
                  ? conflict
                    ?.blockedKeys
                    ?.[0]
                    ?.lockedByName ??
                  null
                  : null,
            });
          }
        )
      );
  }

  releaseLock(
    id: string,
    _userName: string
  ): Observable<
    WareneingangDetail |
    null
  > {
    const key =
      `wareneingang:${id}`;

    const lockSession =
      this.activeLocks.get(
        key
      );

    if (!lockSession) {
      return this.getWareneingangById(
        id
      );
    }

    return this.http
      .delete<void>(
        `${this.apiBaseUrl}/locks/${lockSession.lockId}`
      )
      .pipe(
        tap(() => {
          this.activeLocks.delete(
            key
          );
        }),

        switchMap(() =>
          this.getWareneingangById(
            id
          )
        ),

        catchError(() =>
          this.getWareneingangById(
            id
          )
        )
      );
  }

  saveWareneingang(
    id: string,
    positionen:
      WareneingangPosition[],
    userName: string
  ): Observable<
    WareneingangDetail |
    null
  > {
    const payload =
      positionen
        .filter(
          (position) =>
            position.istMenge !==
            null
        )
        .map(
          (position) => ({
            artikelnummer:
              Number(
                position.artikelnummer
              ),

            istMenge:
              position.istMenge!,
          })
        );

    return this.http
      .put<void>(
        `${this.apiBaseUrl}/wareneingaenge/${id}/positionen`,
        payload
      )
      .pipe(
        switchMap(() =>
          this.releaseLock(
            id,
            userName
          )
        )
      );
  }

  completeWareneingang(
    id: string,
    positionen:
      WareneingangPosition[],
    _userName: string
  ): Observable<
    WareneingangDetail |
    null
  > {
    const payload =
      positionen
        .filter(
          (position) =>
            position.istMenge !==
            null
        )
        .map(
          (position) => ({
            artikelnummer:
              Number(
                position.artikelnummer
              ),

            istMenge:
              position.istMenge!,
          })
        );

    const key =
      `wareneingang:${id}`;

    return this.http
      .put<void>(
        `${this.apiBaseUrl}/wareneingaenge/${id}/positionen`,
        payload
      )
      .pipe(
        switchMap(() =>
          this.http.post<{
            message: string;
          }>(
            `${this.apiBaseUrl}/wareneingaenge/${id}/abschliessen`,
            null
          )
        ),

        tap(() => {
          /*
           * Backend releases the lock
           * when Abschluss succeeds.
           */
          this.activeLocks.delete(
            key
          );
        }),

        switchMap(() =>
          this.getAnzahlOffen()
            .pipe(
              catchError(
                () =>
                  of(
                    undefined
                  )
              )
            )
        ),

        switchMap(() =>
          this.getWareneingangById(
            id
          )
        )
      );
  }

  private mapWareneingang(
    item:
      BackendWareneingangDto
  ): Wareneingang {
    return {
      id:
        item.uid,

      filialNummer:
        item.filialNummer,

      filialeName:
        item.filialName ??
        '',

      lieferscheinnummer:
        item.lieferscheinnummer ??
        '',

      datum:
        this.formatDate(
          item.wareneingangTag
        ),

      wareneingangTag:
        item.wareneingangTag,

      beschreibung:
        item.beschreibung,

      bearbeitungsZeitBis:
        item.bearbeitungsZeitBis,

      status:
        this.mapStatus(
          item.status
        ),

      anzahlPositionen:
        item.anzahlPositionen,

      offenePositionen:
        item.anzahlOffenePositionen,

      fortschritt:
        item.fortschrittProzent,

      faelligkeitLabel:
        this.mapFaelligkeitLabel(
          item.faelligkeit
        ),

      faelligkeitStatus:
        this.mapFaelligkeitStatus(
          item.faelligkeit
        ),

      istGesperrt:
        false,

      gesperrtVon:
        null,
    };
  }

  private mapWareneingangDetail(
    item:
      BackendWareneingangDetailDto
  ): WareneingangDetail {
    const positionen:
      WareneingangPosition[] =
      item.positionen.map(
        (position) => ({
          artikelnummer:
            String(
              position.artikelnummer
            ),

          artikelname:
            position.artikelName ??
            '',

          warengruppe:
            position.warengruppeName ??
            'Ohne Warengruppe',

          liefermenge:
            position.lieferMenge,

          istMenge:
            position.istMenge,

          bestellteMenge:
            position.bestellteMenge,

          offeneMenge:
            position.offeneMenge,

          abweichung:
            position.abweichung,
        })
      );

    const offenePositionen =
      item.positionen.filter(
        (position) =>
          position.offeneMenge !==
          0
      ).length;

    const fortschritt =
      item.anzahlPositionen
        ? Math.round(
          (
            (
              item.anzahlPositionen -
              offenePositionen
            ) /
            item.anzahlPositionen
          ) *
          100
        )
        : 0;

    return {
      id:
        item.uid,

      filialNummer:
        item.filialNummer,

      filialeName:
        item.filialName ??
        '',

      lieferscheinnummer:
        item.lieferscheinnummer ??
        '',

      datum:
        this.formatDate(
          item.wareneingangTag
        ),

      wareneingangTag:
        item.wareneingangTag
          .split('T')[0],

      beschreibung:
        item.beschreibung,

      bearbeitungsZeitBis:
        item.bearbeitungsZeitBis,

      status:
        this.mapStatus(
          item.status
        ),

      anzahlPositionen:
        item.anzahlPositionen,

      offenePositionen,
      fortschritt,

      anzahlKorrekturen:
        item.anzahlKorrekturen,

      faelligkeitLabel:
        '',

      faelligkeitStatus:
        'GEPLANT',

      istGesperrt:
        false,

      gesperrtVon:
        null,

      positionen,
    };
  }

  private mapStatus(
    status:
      BackendWareneingangDto[
      'status'
      ]
  ): WareneingangStatus {
    switch (status) {
      case 'Offen':
        return WareneingangStatus.Offen;

      case 'InBearbeitung':
        return WareneingangStatus.InBearbeitung;

      case 'Abgeschlossen':
        return WareneingangStatus.Abgeschlossen;
    }
  }

  private mapFaelligkeitStatus(
    faelligkeit:
      BackendWareneingangDto[
      'faelligkeit'
      ]
  ):
    | 'UEBERFAELLIG'
    | 'HEUTE'
    | 'GEPLANT' {
    switch (
    faelligkeit
    ) {
      case 'Ueberfaellig':
        return 'UEBERFAELLIG';

      case 'HeuteFaellig':
        return 'HEUTE';

      case 'Zukuenftig':
      case 'Keine':
        return 'GEPLANT';
    }
  }

  private mapFaelligkeitLabel(
    faelligkeit:
      BackendWareneingangDto[
      'faelligkeit'
      ]
  ): string {
    switch (
    faelligkeit
    ) {
      case 'Ueberfaellig':
        return 'Überfällig';

      case 'HeuteFaellig':
        return 'Heute fällig';

      case 'Zukuenftig':
        return 'Geplant';

      case 'Keine':
        return '';
    }
  }

  private formatDate(
    value: string
  ): string {
    if (!value) {
      return '';
    }

    const date =
      new Date(
        value
      );

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return value;
    }

    return new Intl.DateTimeFormat(
      'de-DE',
      {
        day:
          '2-digit',

        month:
          '2-digit',

        year:
          'numeric',
      }
    ).format(
      date
    );
  }

  heartbeatLock(
    id: string
  ): Observable<
    WareneingangLockSession |
    null
  > {
    const key =
      `wareneingang:${id}`;

    const lockSession =
      this.activeLocks.get(
        key
      );

    if (!lockSession) {
      return of(null);
    }

    return this.http
      .put<
        WareneingangLockSession
      >(
        `${this.apiBaseUrl}/locks/${lockSession.lockId}/heartbeat`,
        null
      )
      .pipe(
        tap((response) =>
          this.activeLocks.set(
            key,
            response
          )
        )
      );
  }

  getLockSession(
    id: string
  ): WareneingangLockSession | null {
    return (
      this.activeLocks.get(
        `wareneingang:${id}`
      ) ?? null
    );
  }

  clearLock(
    id: string
  ): void {
    this.activeLocks.delete(
      `wareneingang:${id}`
    );
  }

  getVerfuegbareArtikel(
    id: string
  ): Observable<
    ArtikelkatalogEintrag[]
  > {
    return this.http
      .get<
        BackendVerfuegbarerArtikelDto[]
      >(
        `${this.apiBaseUrl}/wareneingaenge/${id}/verfuegbare-artikel`
      )
      .pipe(
        map((artikel) =>
          artikel.map(
            (item) => ({
              artikelnummer:
                String(
                  item.artikelnummer
                ),

              artikelname:
                item.artikelName ??
                '',

              warengruppe:
                '',

              lieferEinheitName:
                item.lieferEinheitName,
              einheiten: normalizeArtikelEinheiten(item.einheiten),
            })
          )
        )
      );
  }

  addArtikel(
    id: string,
    artikelnummer: number
  ): Observable<
    WareneingangDetail |
    null
  > {
    return this.http
      .post<void>(
        `${this.apiBaseUrl}/wareneingaenge/${id}/positionen/${artikelnummer}`,
        null
      )
      .pipe(
        switchMap(() =>
          this.getWareneingangById(
            id
          )
        )
      );
  }
}